/**
 * useActiveMenuItem — pozycja menu, przez którą klient wszedł na stronę kartoteki.
 *
 * Sidebar dokleja `?mi=<id>` do linku pozycji, która ma przypisany widok (layout
 * listy/formularza lub filtry stałe). Strony klienta (`ModuleRecordsPage`, `RecordFormPage`,
 * listy kartotek) czytają tę pozycję, żeby honorować jej przypisania w runtime.
 *
 * Pozycja pochodzi z DWÓCH źródeł: z aktywnego menu własnego klienta (`MenuItem.id`) albo z
 * menu modułu dostarczonego z kreatora ITE (`InstalledMenuItem.menuItemId` — wskaźniki układów
 * są już w formacie klienta `'<slug kartoteki>:<id układu>'`). Oba zapytania są w cache React
 * Query (pobiera je Sidebar), więc tu tylko odczytujemy — bez dodatkowego round-tripu.
 */

import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { MenuItem } from '@saas/shared';

import type { InstalledMenuArea, InstalledMenuItem } from '@/components/Sidebar';
import { findInTree } from '@/lib/menu-tree';

import { useActiveMenuLayout } from './useActiveMenuLayout';
import { useInstalledModulesMenuQuery } from './useInstalledModulesMenuQuery';

function findInstalledItem(
  areas: InstalledMenuArea[],
  menuItemId: string,
): InstalledMenuItem | null {
  const walk = (items: InstalledMenuItem[]): InstalledMenuItem | null => {
    for (const item of items) {
      if (item.menuItemId === menuItemId) return item;
      const inChildren = item.children ? walk(item.children) : null;
      if (inChildren) return inChildren;
    }
    return null;
  };
  for (const area of areas) {
    const found = walk(area.items);
    if (found) return found;
  }
  return null;
}

export function useActiveMenuItem(): MenuItem | null {
  const [searchParams] = useSearchParams();
  const menuItemId = searchParams.get('mi');
  const { data } = useActiveMenuLayout({ enabled: !!menuItemId });
  const { data: areas } = useInstalledModulesMenuQuery({ enabled: !!menuItemId });

  return useMemo(() => {
    if (!menuItemId) return null;
    const own = findInTree(data?.layout?.hierarchy ?? [], menuItemId);
    if (own) return own;
    const installed = areas ? findInstalledItem(areas, menuItemId) : null;
    if (!installed) return null;
    return {
      id: menuItemId,
      type: 'module',
      ...(installed.viewRef ? { viewRef: installed.viewRef } : {}),
      ...(installed.listRef ? { listRef: installed.listRef } : {}),
      ...(installed.view ? { view: installed.view } : {}),
    };
  }, [menuItemId, data, areas]);
}
